import { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import * as API from "../apiConfig";
import {
    OrderShareSuccessModal,
    ShareOnWhatsAppIconButton,
    shareOrderOnWhatsApp,
} from "./OrderShareWhatsApp";

function OrdersByStage() {
    const { stage } = useParams();
    const decodedStage = decodeURIComponent(stage);

    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [counts, setCounts] = useState([]);
    const [loading, setLoading] = useState(true);

    const { authData } = useOutletContext();
    const authToken = authData?.token;

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editedOrder, setEditedOrder] = useState({});
    const [saving, setSaving] = useState(false);

    const [orders, setOrders] = useState([]);
    const [showOrderSuccessModal, setShowOrderSuccessModal] = useState(false);
    const [orderSuccessMessage, setOrderSuccessMessage] = useState("Order updated successfully");
    const [orderShareClientId, setOrderShareClientId] = useState("");
    const [orderShareOrder, setOrderShareOrder] = useState(null);

    const [previewUrl, setPreviewUrl] = useState(null);

    const FIXED_STAGES = [
        "Pending", "Processing", "Cutting", "Polishing", "Glass Cutting With Frosted", "Glass Cutting",
        "Dispatched for Coloring", "Returned from Coloring",
        "Completed", "Delivered", "Billed"
    ];

    const getRequestHeaders = useCallback(() => {
        const headers = { "Content-Type": "application/json" };
        if (authToken) headers.Authorization = `Bearer ${authToken}`;
        return headers;
    }, [authToken]);

    const fetchCounts = useCallback(() => {
        fetch(API.DASHBOARD_COUNTS_URL, {
            headers: getRequestHeaders()
        })
            .then(res => res.json())
            .then(res => {
                const map = new Map();
                res.forEach(i => map.set(i.status.toLowerCase(), i.count));

                const normalized = FIXED_STAGES.map(stage => ({
                    status: stage,
                    count: map.get(stage.toLowerCase()) || 0
                }));

                setCounts(normalized);
            });
    }, [getRequestHeaders]);

    // ===== FETCH ORDERS =====
    useEffect(() => {
        setLoading(true);

        fetch(`${API.ORDERS_API_URL}/stage?stage=${encodeURIComponent(decodedStage)}`, {
            headers: getRequestHeaders()
        })
            .then(res => res.json())
            .then(res => {
                setData(res);
                setLoading(false);
            })
            .catch(() => setLoading(false));

    }, [decodedStage, getRequestHeaders]);

    // ===== FETCH COUNTS =====
    useEffect(() => {
        fetchCounts();
    }, [getRequestHeaders]);

    // ===== DELETE =====
    const handleDeleteOrder = async (orderId) => {
        const input = prompt("Enter Order ID to confirm deletion:");
        if (input === null) return;

        if (input.trim() !== String(orderId)) {
            alert("Wrong Order ID. Deletion cancelled.");
            return;
        }

        const res = await fetch(`${API.ORDERS_API_URL}/${orderId}`, {
            method: "DELETE",
            headers: getRequestHeaders(),
        });

        if (res.ok) {
            setData(prev => ({
                ...prev,
                orders: prev.orders.filter(o => o.id !== orderId)
            }));
            fetchCounts();
        } else {
            alert("Failed to delete");
        }
    };

    const closeOrderSuccessModal = () => {
        setShowOrderSuccessModal(false);
    };

    const handleShareOnWhatsApp = async (clientId, order) => {
        try {
            await shareOrderOnWhatsApp({
                clientId,
                order,
                getRequestHeaders,
            });
        } catch (error) {
            alert(error.message || "Unable to open WhatsApp share");
        }
    };

    // ===== STAGE CHANGE =====
    const handleStageChange = async (orderId, newStage) => {
        try {
            const currentOrder = data?.orders?.find((order) => order.id === orderId);
        let stageToSend = newStage;
        let mode = null;

        if (
            newStage === "Delivered via Porter" ||
            newStage === "Client Picked Up"
        ) {
            stageToSend = "Delivered";
            mode = newStage;
        }

        let url = `${API.ORDERS_API_URL}/${orderId}/stage?stage=${encodeURIComponent(stageToSend)}`;
        if (mode) url += `&mode=${encodeURIComponent(mode)}`;

        const response = await fetch(url, {
            method: "PUT",
            headers: getRequestHeaders(),
        });
        if (!response.ok) {
            throw new Error("Unable to update order stage");
        }

        if (currentOrder?.clientId) {
            setOrderShareClientId(String(currentOrder.clientId));
            setOrderShareOrder({
                ...currentOrder,
                currentStage: newStage,
                mode: mode || currentOrder.mode || "",
            });
            setOrderSuccessMessage("Order updated successfully");
            setShowOrderSuccessModal(true);
        }

        // update table
        setData(prev => ({
            ...prev,
            orders: prev.orders.filter(o => o.id !== orderId)
        }));

        // 🔥 IMPORTANT: refresh counts
            fetchCounts();
        } catch (error) {
            alert(error.message || "Failed to update stage");
        }
    };
    const handleEditOrder = (order) => {
        setSelectedOrder(order);

        setEditedOrder({
            description: order.description || '',
            height: order.height || '',
            width: order.width || '',
            quantity: order.quantity || '',
            rate: order.rate || '',
            amount: order.amount || '',
            currentStage: order.currentStage || '',
            totalSquareFeet: order.totalSquareFeet || '',
            imagePath: order.imagePath || ''
        });

        setShowEditModal(true);
    };



    const handleFieldChange = (field, value) => {
        const updatedOrder = {
            ...editedOrder,
            [field]: value
        };

        const height = parseFloat(updatedOrder.height) || 0;
        const width = parseFloat(updatedOrder.width) || 0;
        const quantity = parseFloat(updatedOrder.quantity) || 0;
        const rate = parseFloat(updatedOrder.rate) || 0;

        // Recalculate total sq ft if dimensions/quantity change
        if (["height", "width", "quantity"].includes(field)) {
            updatedOrder.totalSquareFeet = ((height * width) / 144) * quantity;
        }

        // Recalculate amount in ALL cases where it should depend on sq ft
        if (
            ["height", "width", "quantity", "rate", "totalSquareFeet"].includes(field)
        ) {
            const totalSqFt =
                parseFloat(updatedOrder.totalSquareFeet) ||
                ((height * width) / 144) * quantity;

            updatedOrder.amount = totalSqFt * rate;
        }

        setEditedOrder(updatedOrder);
    };

    const handleEditImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // preview
        const preview = URL.createObjectURL(file);
        setPreviewUrl(preview);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const res = await fetch(`${API.ORDERS_API_URL}/upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${authToken}`
                },
                body: formData
            });

            const url = await res.text();

            // IMPORTANT: update editedOrder so payload includes it
            setEditedOrder(prev => ({
                ...prev,
                imagePath: url
            }));

        } catch (err) {
            console.error("Image upload failed", err);
        }
    };

    const submitOrderUpdate = async () => {
        if (!selectedOrder) return;

        setSaving(true);
        try {
            const payload = {};

            Object.keys(editedOrder).forEach(key => {
                if (editedOrder[key] !== selectedOrder[key]) {
                    let value = editedOrder[key];

                    // convert numeric fields to number
                    if (
                        ["height", "width", "quantity", "rate", "amount", "totalSquareFeet"]
                            .includes(key)
                    ) {
                        value = parseFloat(value);
                        if (isNaN(value)) value = 0;
                    }

                    if (key === "imagePath" || editedOrder[key] !== selectedOrder[key]) {
                        payload[key] = value;
                    }
                }
            });

            if (Object.keys(payload).length === 0) {
                setShowEditModal(false);
                return;
            }
            console.log(JSON.stringify(payload));
            const res = await fetch(`${API.ORDERS_API_URL}/${selectedOrder.id}`, {
                method: 'PUT',
                headers: getRequestHeaders(),
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (res.ok) {
                if (res.ok) {
                    setData(prev => ({
                        ...prev,
                        orders: prev.orders.map(o =>
                            o.id === data.order.id ? data.order : o
                        )
                    }));
                    setShowEditModal(false);
                }
                setShowEditModal(false);
            } else {
                alert(data.error || "Update failed");
            }

        } catch (err) {
            alert("Error updating order");
        } finally {
            setSaving(false);
        }
    };

    // ===== HEADER =====
    return (
        <>

            {/* STAGE SWITCHER */}
            <div className="card border-0 shadow-sm my-5">
                <div className="card-body p-3">

                    <div className="small text-muted mb-2">Switch Stage</div>

                    <div className="row g-2">
                        {counts.map((item, i) => (
                            <div key={i} className="col-6 col-md-3">
                                <div
                                    className="p-2 border rounded text-center"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        navigate(`/home/orders/stage/${encodeURIComponent(item.status)}`)
                                    }
                                >
                                    <div className="small">{item.status}</div>
                                    <div className="fw-semibold">{item.count}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
            <div className="container-fluid">


                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-0 fw-semibold">{decodedStage}</h5>
                        <small className="text-muted">
                            {data?.count || 0} orders
                        </small>
                    </div>
                </div>

                {/* LOADING */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border spinner-border-sm" />
                    </div>
                ) : data?.orders?.length === 0 ? (

                    /* EMPTY */
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body text-center py-5">
                            <div className="fw-semibold">No orders found</div>
                            <div className="text-muted small">
                                No orders in this stage
                            </div>
                        </div>
                    </div>

                ) : (

                    /* TABLE VIEW (same as Orders.jsx) */
                    <div className="table-responsive mb-4">
                        <table className="table table-hover align-middle">

                            <thead className="table-light">
                                <tr>
                                    <th>ID</th>
                                    <th>Img</th>
                                    <th>QR</th>
                                    <th className="d-none d-lg-table-cell">Size</th>
                                    <th className="d-none d-lg-table-cell">Rate</th>
                                    <th>Square Foots</th>
                                    <th>Description</th>
                                    <th>Client</th>
                                    <th>Qty</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Billed</th>
                                    <th>Actions</th>
                                    <th>Share</th>
                                </tr>
                            </thead>

                            <tbody>
                                {data.orders.map(order => (
                                    <tr key={order.id}>

                                        <td
                                            onClick={() => navigate(`/orders/${order.id}`)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {order.id}
                                        </td>

                                        <td>
                                            {order.imagePath && (
                                                <img
                                                    src={order.imagePath}
                                                    style={{ width: 50, height: 50, objectFit: "cover" }}
                                                    className="rounded"
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {order.qrCodePath ? (
                                                <img
                                                    src={order.qrCodePath}
                                                    alt=""
                                                    style={{ width: 50, height: 50, objectFit: "cover" }}
                                                    className="rounded"
                                                />
                                            ) : "—"}
                                        </td>

                                        <td className="d-none d-lg-table-cell">
                                            {order.height} x {order.width}
                                        </td>

                                        <td className="d-none d-lg-table-cell">
                                            {Number(order.rate || 0).toFixed(2)}
                                        </td>

                                        <td>
                                            {Number(order.totalSquareFeet || 0).toFixed(2)}
                                        </td>

                                        <td>{order.description}</td>
                                        <td>{order.clientName}</td>
                                        <td>{order.quantity}</td>
                                        <td>₹{order.amount}</td>

                                        <td>
                                            {order.billed ? (
                                                <span className="badge bg-secondary">Locked</span>
                                            ) : (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={
                                                        order.currentStage === "Delivered"
                                                            ? order.mode || "Delivered"
                                                            : order.currentStage
                                                    }
                                                    onChange={(e) =>
                                                        handleStageChange(order.id, e.target.value)
                                                    }
                                                >
                                                    <option>Pending</option>
                                                    <option>Processing</option>
                                                    <option>Cutting</option>
                                                    <option>Polishing</option>
                                                    <option>Glass Cutting With Frosted</option>
                                                    <option>Glass Cutting</option>
                                                    <option>Dispatched for Coloring</option>
                                                    <option>Returned from Coloring</option>
                                                    <option>Completed</option>
                                                    <option>Delivered via Porter</option>
                                                    <option>Client Picked Up</option>
                                                </select>
                                            )}
                                        </td>

                                        <td>
                                            {order.billed ? (
                                                <span className="badge bg-warning text-dark">Billed</span>
                                            ) : (
                                                <span className="badge bg-light text-muted">No</span>
                                            )}
                                        </td>

                                        <td>
                                            {order.billed ? (
                                                <span className="badge bg-secondary">Locked</span>
                                            ) : (
                                                <div className="btn-group btn-group-sm">
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => handleEditOrder(order)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger"
                                                        onClick={() => handleDeleteOrder(order.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <ShareOnWhatsAppIconButton
                                                label={`Share order ${order.id} on WhatsApp`}
                                                onClick={() => handleShareOnWhatsApp(order.clientId, order)}
                                            />
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}




            </div>

            <OrderShareSuccessModal
                show={showOrderSuccessModal}
                message={orderSuccessMessage}
                onClose={closeOrderSuccessModal}
                onShare={() => handleShareOnWhatsApp(orderShareClientId, orderShareOrder)}
            />

            {showEditModal && (
                <div
                    className="modal fade show d-block"
                    style={{ background: 'rgba(0,0,0,0.5)', zIndex: 2000 }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-fullscreen-sm-down">
                        <div className="modal-content rounded-4">

                            {/* Header */}
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Order</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>

                            {/* Body */}
                            <div className="modal-body d-flex flex-column gap-3">

                                {/* Description */}
                                <div>
                                    <label className="form-label small text-muted">Description</label>
                                    <input
                                        className="form-control"
                                        value={editedOrder.description}
                                        onChange={e => handleFieldChange("description", e.target.value)}
                                    />
                                </div>

                                {/* Dimensions */}
                                <div>
                                    <label className="form-label small text-muted">
                                        Dimensions (Height × Width in inches)
                                    </label>
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Height"
                                                value={editedOrder.height}
                                                onChange={e => handleFieldChange("height", e.target.value)}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Width"
                                                value={editedOrder.width}
                                                onChange={e => handleFieldChange("width", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Quantity */}
                                <div>
                                    <label className="form-label small text-muted">Quantity</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={editedOrder.quantity}
                                        onChange={e => handleFieldChange("quantity", e.target.value)}
                                    />
                                </div>

                                {/* Rate */}
                                <div>
                                    <label className="form-label small text-muted">
                                        Rate (₹ per sq ft)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={editedOrder.rate}
                                        onChange={e => handleFieldChange("rate", e.target.value)}
                                    />
                                </div>

                                {/* Total Sq Ft */}
                                <div>
                                    <label className="form-label small text-muted">
                                        Total Sq Ft
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={editedOrder.totalSquareFeet}
                                        onChange={e => handleFieldChange("totalSquareFeet", e.target.value)}
                                    />
                                </div>

                                {/* Amount */}
                                <div>
                                    <label className="form-label small text-muted">Amount (₹)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={editedOrder.amount}
                                        onChange={e => handleFieldChange("amount", e.target.value)}
                                    />
                                </div>

                                {/* Image Preview */}
                                <div>
                                    <label className="form-label small text-muted">Image</label>

                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={handleEditImageChange}
                                    />

                                    <div className="mt-2 text-center">
                                        <img
                                            src={previewUrl || editedOrder.imagePath}
                                            alt="preview"
                                            style={{
                                                width: "100%",
                                                maxHeight: "200px",
                                                objectFit: "contain",
                                                borderRadius: "8px",
                                                background: "#f5f5f5"
                                            }}
                                        />
                                    </div>
                                </div>

                            </div>

                            {/* Footer */}
                            <div className="modal-footer">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowEditModal(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-dark"
                                    onClick={submitOrderUpdate}
                                    disabled={saving}
                                >
                                    {saving ? "Saving..." : "Save Changes"}
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}</>
    );
}

export default OrdersByStage;
